import {Box, Typography} from '@mui/material';
import {t} from 'i18next';

import AurBreadcrumb from '../../../common/aur-breadcrumb/AurBreadCrumb';
import AurButton from '../../../common/aur-button/AurButton';
import {ReactComponent as ViewDoc} from '../../../../../src/assests/svg/viewDoc.svg';
import {ReactComponent as FolderNoResult} from '../../../../../src/assests/svg/folderNoResult.svg';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
  isUpdatedAfterRunTime,
  unwrapQueryText,
} from '../../../../utils/utils';

import AurIcon, {IconNames, IconColors} from '../../../common/aur-icon/AurIcon';
import {SavedSearchDetails} from '../SavedSearchesTab';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {useCallback, useEffect, useRef, useState} from 'react';
import {
  getSavedSearchDetails,
  savedSearchedRun,
} from '../../../../api/EcaServices';
import {AxiosError} from 'axios';
import {TrackItemsType} from '../../../eca-doc-list/eca-save-search/AurLocationTreeView';
import {useNavigate} from 'react-router-dom';
import {
  SSE_ENDPOINT_ECA,
  SSE_SERVICE_TYPE,
} from '../../../../constants/constants';
import {AurLoadingButton} from '../../../common/aur-loading-button/AurLoadingButton';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';

interface SavedSearchProps {
  node?: string | null;
  details: SavedSearchDetails;
  expandedIds?: string[];
  breadcrumbItems: TrackItemsType[];
  showSavedSearchLoader: boolean;
}

const SavedSearchRightPanel: React.FC<SavedSearchProps> = ({
  node,
  details,
  breadcrumbItems,
  expandedIds = [''],
  showSavedSearchLoader = false,
}) => {
  const {clientId, projectId, selectedProjectName} = useSelector(
    (state: RootState) => state.reports
  );
  const {showToast} = useToast();
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const [loadingLastDocCount, setLoadingLastDocCount] =
    useState<boolean>(false);
  const [savedSearchedDetails, setSavedSearchedDetails] =
    useState<SavedSearchDetails>(details);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  const {hasSearchesPerm} = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const panelHeader = useCallback(() => {
    const breadCrumb: {label: string; path?: string}[] = [
      {
        label: selectedProjectName ?? '',
      },
    ];
    breadcrumbItems.forEach((item) => {
      breadCrumb.push({label: item.label, path: ''});
    });
    return (
      <div className="panel-breadcrumb">
        <AurBreadcrumb items={breadCrumb} />
      </div>
    );
  }, [breadcrumbItems]);

  useEffect(() => {
    setSavedSearchedDetails(details);
  }, [details]);

  const fetchSavedSearchDetails = async (id: string) => {
    setLoadingLastDocCount(true);
    if (clientId && projectId) {
      try {
        const params = `?clientId=${clientId}&projectId=${projectId}`;
        const result = await getSavedSearchDetails(params, id);
        setSavedSearchedDetails(result.data.data);
        setLoadingLastDocCount(false);
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center', undefined, {
          code: err?.code,
        });
        setLoadingLastDocCount(false);
      }
    }
  };

  const handleSavedSearchRun = async () => {
    setLoadingLastDocCount(true);
    const title = savedSearchedDetails.fieldTemplateId?.toString();
    const ecaSearchId = savedSearchedDetails.ecaSearchId;
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      if (clientId && projectId && ecaSearchId) {
        const url =
          `/indexes/${title}/savedSearches/${ecaSearchId}/run` + params;
        if (abortControllers.current.has(url)) {
          abortControllers.current.get(url)?.abort();
        }
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        const controller = new AbortController();
        abortControllers.current.set(url, controller);
        const result = await savedSearchedRun(
          params,
          title,
          ecaSearchId,
          controller.signal
        );
        getSearchIdSse(result.data?.data?.requestId);
        abortControllers.current.delete(url);
      }
    } catch (error: any) {
      setLoadingLastDocCount(false);
      if (error.name === 'CanceledError') {
        return;
      }
      abortControllers.current.delete(
        `/indexes/${title}/savedSearches/${ecaSearchId}/run` + params
      );
    } finally {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.ecaHomePage.updateDocBtn'),
          region: GA_REGIONS.WIDGET_BODY,
          type: GA_CLICK_TYPES.BUTTON,
          action: t('gaTracking.ecaHomePage.updatedDocumentCount'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.savedSearches'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
  };

  useEffect(() => {
    setLoadingLastDocCount(false);
  }, [node]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      abortControllers.current.forEach((controller) => {
        controller.abort();
      });
      abortControllers.current.clear();
    };
  }, [savedSearchedDetails.ecaSearchId]);

  const eventSourceRef = useRef<EventSource | null>(null);
  const getSearchIdSse = useCallback(
    (requestId: string) => {
      if (requestId && requestId != '') {
        const SSE_URL = `${SSE_ENDPOINT_ECA}/sse/${requestId}/${SSE_SERVICE_TYPE.SEARCH_SERVICE}`;
        eventSourceRef.current = new EventSource(SSE_URL);
        eventSourceRef.current.onmessage = (event) => {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
          }
          const responseData = JSON.parse(event.data);
          if (responseData.status.toLowerCase() == 'completed') {
            fetchSavedSearchDetails(savedSearchedDetails.id);
          } else {
            showToast(
              t('common.somethingWrong'),
              'error',
              undefined,
              'center',
              undefined
            );
            setLoadingLastDocCount(false);
          }

          if (eventSourceRef.current) {
            eventSourceRef.current.close();
          }
        };

        eventSourceRef.current.onerror = () => {
          setLoadingLastDocCount(false);
          showToast(
            t('common.somethingWrong'),
            'error',
            undefined,
            'center',
            undefined
          );
          if (eventSourceRef.current) {
            eventSourceRef.current.close(); // Close previous connection if it exists
          }
        };
      }
    },
    [savedSearchedDetails.ecaSearchId]
  );

  const checkLastRun = () => {
    if (
      savedSearchedDetails.applyFilter &&
      savedSearchedDetails.filteredDocCount != null &&
      savedSearchedDetails.filterSet?.items?.length > 0
    ) {
      return true; // If filters are applied and filteredDocCount is available, use it for last run count
    } else {
      if (
        savedSearchedDetails.lastRunDetail?.lastRunTime &&
        savedSearchedDetails.lastRunDetail?.lastRunTime != ''
      ) {
        return isUpdatedAfterRunTime(
          savedSearchedDetails.updatedDetail.updatedTime,
          savedSearchedDetails.lastRunDetail?.lastRunTime
        );
      }
      return false;
    }
  };

  if (!node) {
    return (
      <>
        {showSavedSearchLoader ? (
          <Box className="saved-search-right-panel-loader">
            <AurLoadingIndicator show={true} />
          </Box>
        ) : (
          <Box className="right-panel">
            {panelHeader()}
            <Box className="no-search-wrapper">
              <Box className="no-search-content">
                <FolderNoResult aria-hidden="true"/>
                <Typography className="typo-text headline2_size color_midnight_11000">
                  {t(
                    'ecaHomepageModals.tabsRightPanel.savedSearch.noResultTitle'
                  )}
                </Typography>
                <Typography className="typo-text body3_size">
                  {t(
                    'ecaHomepageModals.tabsRightPanel.savedSearch.noResultSubTitle'
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </>
    );
  }
  let documentCount = savedSearchedDetails.docCount;

  if (
    savedSearchedDetails.applyFilter &&
    savedSearchedDetails.filteredDocCount != null &&
    savedSearchedDetails.filterSet?.items?.length > 0
  ) {
    documentCount = savedSearchedDetails.filteredDocCount;
  }

  const renderDateTimeWithTooltip = (value?: string) => {
    return (
      <AurLightTooltip
        title={t('common.yearMonthDate')}
        placement="bottom-start"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -6], // move tooltip closer
                },
              },
            ],
          },
        }}
      >
        <Typography className="typo-text body4_size color_midnight_600">
          {value ? getUTCDateTimeForDisplay(value) : ''}
        </Typography>
      </AurLightTooltip>
    );
  };

  return (
    <>
      {showSavedSearchLoader ? (
        <Box className="saved-search-right-panel-loader">
          <AurLoadingIndicator show={true} />
        </Box>
      ) : (
        <Box className="right-panel">
          {panelHeader()}

          <Box
            className="right-panel-title"
            display="flex"
            justifyContent="space-between"
          >
            <Typography className="typo-text headline2_size color_mist_100 custom-text-wrap">
              {savedSearchedDetails.name}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Typography className="typo-text body2_size search-id">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.searchId')}
              </Typography>
              <Typography className="typo-text label1_size search-number color_mist_100">
                {savedSearchedDetails.ecaSearchId}
              </Typography>
            </Box>
          </Box>

          <Box className="right-panel-details">
            <Box>
              <Typography className="typo-text subhead4_size color_midnight_11000">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.lastCount')}
              </Typography>
              <Box
                display="flex"
                justifyContent="space-between"
                className="count-details"
              >
                <Typography className="typo-text display1_size last-count">
                  {!isNaN(documentCount) && checkLastRun()
                    ? formatNumberWithComma(documentCount)
                    : '--'}
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  {
                    <AurLoadingButton
                      ariaLabel={t(
                        'ecaHomepageModals.tabsRightPanel.savedSearch.updateDocBtn'
                      )}
                      classNames="typo-text buttonMedium_size"
                      onClick={() => {
                        node && handleSavedSearchRun();
                      }}
                      loading={loadingLastDocCount}
                      buttonType="submit"
                      size="Medium"
                      dataTestId="update-doc-count"
                      title={t(
                        'ecaHomepageModals.tabsRightPanel.savedSearch.updateDocBtn'
                      )}
                      label={t(
                        'ecaHomepageModals.tabsRightPanel.savedSearch.updateDocBtn'
                      )}
                    />
                  }
                  {hasSearchesPerm && (
                    <AurButton
                      ariaLabel={t(
                        'ecaHomepageModals.tabsRightPanel.savedSearch.viewDocBtn'
                      )}
                      classNames="typo-text buttonMedium_size"
                      onClick={() => {
                        if (savedSearchedDetails) {
                          navigate('/index-search', {
                            state: {
                              source: 'savedSearch',
                              selected: {
                                ...savedSearchedDetails,
                                query: unwrapQueryText(
                                  savedSearchedDetails.query
                                ),
                              },
                              expanded: expandedIds,
                            },
                          });
                          trackGAEvent({
                            eventName: GA_EVENT_NAMES.SEARCH,
                            click: {
                              name: t('gaTracking.ecaHomePage.viewDocBtn'),
                              region: GA_REGIONS.WIDGET_BODY,
                              type: GA_CLICK_TYPES.BUTTON,
                              action: t(
                                'gaTracking.ecaHomePage.viewedDocumentsInSavedSearch'
                              ),
                            },
                            subPage: {
                              subpage_name: t(
                                'gaTracking.ecaHomePage.savedSearches'
                              ),
                              subpage_type: GA_SUBPAGE_TYPE.TAB,
                            },
                          });
                        }
                      }}
                      type="continue"
                      size="Small"
                      startIcon={<ViewDoc aria-hidden />}
                      dataTestId="view-docs"
                    >
                      {t(
                        'ecaHomepageModals.tabsRightPanel.savedSearch.viewDocBtn'
                      )}
                    </AurButton>
                  )}
                </Box>
              </Box>
            </Box>
            <Box className="meta-details" display="flex">
              <Box flex={1}>
                <Typography className="typo-text label2_size color_midnight_600">
                  {t('ecaHomepageModals.tabsRightPanel.savedSearch.createdBy')}
                </Typography>
                <Typography
                  className="typo-text headline3_size color_midnight_11000"
                  style={{marginTop: '10px'}}
                >
                  {savedSearchedDetails.createdDetail?.createdUserName}
                </Typography>
                {renderDateTimeWithTooltip(
                  savedSearchedDetails.createdDetail?.createdTime
                )}
              </Box>
              <Box flex={1}>
                <Typography className="typo-text label2_size color_midnight_600">
                  {t(
                    'ecaHomepageModals.tabsRightPanel.savedSearch.lastModified'
                  )}
                </Typography>
                <Typography
                  className="typo-text headline3_size color_midnight_11000"
                  style={{marginTop: '10px'}}
                >
                  {savedSearchedDetails.updatedDetail?.updatedUserName}
                </Typography>
                {renderDateTimeWithTooltip(
                  savedSearchedDetails.updatedDetail?.updatedTime
                )}
              </Box>
              <Box flex={1}>
                <Typography className="typo-text label2_size color_midnight_600">
                  {t('ecaHomepageModals.tabsRightPanel.savedSearch.lastRun')}
                </Typography>
                {checkLastRun() ? (
                  <>
                    <Typography
                      className="typo-text headline3_size color_midnight_11000"
                      style={{marginTop: '10px'}}
                    >
                      {savedSearchedDetails.lastRunDetail?.lastRunUserName
                        ? savedSearchedDetails.lastRunDetail?.lastRunUserName
                        : ''}
                    </Typography>
                    {renderDateTimeWithTooltip(
                      savedSearchedDetails.lastRunDetail?.lastRunTime
                    )}
                  </>
                ) : null}
              </Box>
            </Box>
            <Box className="meta-details">
              <Typography className="typo-text subhead3_size">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.notes')}
              </Typography>
              <Typography className="typo-text body3_size custom-text-wrap">
                {savedSearchedDetails.notes}
              </Typography>
            </Box>
            <Box className="meta-details">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography className="typo-text subhead3_size">
                  {t(
                    'ecaHomepageModals.tabsRightPanel.savedSearch.searchQuery'
                  )}
                </Typography>
                <AurButton
                  ariaLabel={'Copy Syntax'}
                  classNames="typo-text buttonMedium_size copy-syntax"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      unwrapQueryText(savedSearchedDetails.query)
                    );
                    trackGAEvent({
                      eventName: GA_EVENT_NAMES.CLICK,
                      click: {
                        name: t('gaTracking.ecaHomePage.copySyntax'),
                        region: GA_REGIONS.WIDGET_BODY,
                        type: GA_CLICK_TYPES.BUTTON,
                        action: t(
                          'gaTracking.ecaHomePage.copiedSearchQuerySyntax'
                        ),
                      },
                      subPage: {
                        subpage_name: t('gaTracking.ecaHomePage.savedSearches'),
                        subpage_type: GA_SUBPAGE_TYPE.TAB,
                      },
                    });
                  }}
                  type="exit"
                  size="Medium"
                  startIcon={
                    <AurIcon
                      icon={IconNames.ContentCopyOutlined}
                      color={IconColors.Custom}
                      size="medium"
                      customClassName="copy-icon"
                    />
                  }
                  dataTestId="copy-syntax"
                >
                  {t('ecaHomepageModals.tabsRightPanel.savedSearch.copySyntax')}
                </AurButton>
              </Box>
              <Typography
                className="typo-text body3_size custom-text-wrap"
                style={{marginTop: '10px'}}
              >
                {savedSearchedDetails.query &&
                  unwrapQueryText(savedSearchedDetails.query)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default SavedSearchRightPanel;
